/**
 * La hoja de vida por piezas: `/job-apply/api/profile` (D9 y D10 del spec
 * 2026-10-21-tbit-jobs-hoja-de-vida-por-piezas).
 *
 * El servidor guarda primero lo que dijo la persona y después lo procesa con el
 * modelo, así que toda escritura responde con la vista nueva y el puntaje de
 * antes y de ahora: la pantalla no vuelve a pedir nada para pintar el cambio.
 *
 * Un estado y no un `throw`, como el cliente del puntaje: `no_profile` no es un
 * error (alguien que todavía no tiene hoja de vida) y `profile_busy` tampoco
 * (otra pestaña guardaba al mismo tiempo; reintentar es lo correcto). Los
 * mensajes salen del idioma de la app y no del cuerpo: el servidor solo habla
 * español.
 */
import { apiFetch } from '~/lib/api';
import { messages } from '~/i18n';
import type { InputMode } from '../lib/voice';

export const PIECE_KINDS = [
  'job',
  'education',
  'license',
  'certification',
  'skills',
  'languages',
  'physical',
  'other',
] as const;
export type PieceKind = (typeof PIECE_KINDS)[number];

export interface PieceNote {
  id: string;
  text: string;
  source: 'questionnaire' | 'gap_answer' | 'upload' | 'edit' | 'recovered';
  gap_id?: string;
  at: string;
}

/** Una pieza como la manda `GET /profile`: sin huellas ni calificación cruda. */
export interface ProfilePiece {
  id: string;
  kind: PieceKind;
  /** Por tipo, con las llaves de `lib/profile/model.ts` del servicio. */
  fields: Record<string, unknown>;
  /** Las palabras de la persona. */
  text: string;
  notes: PieceNote[];
  /** Las viñetas en inglés, como salen en la hoja de vida; null si aún no se escribieron. */
  rendered_preview: string | null;
  stale: boolean;
  /** 'recovered': nació de una versión anterior del cuestionario (D11). */
  source?: PieceNote['source'];
  gap_id?: string;
}

export interface ProfileGap {
  gap_id: string;
  piece_id: string | null;
  gap_kind: string;
  question: string;
  why_it_matters: string;
  how_to_fix: string;
  contradiction: boolean;
}

export interface ProfileView {
  basics: Record<string, unknown>;
  pieces: ProfilePiece[];
  gaps: ProfileGap[];
  /** La forma de `cv_score_detail` (ver `normalizeScoreDetail`); null antes del primer puntaje. */
  score: Record<string, unknown> | null;
  score_history: { score: number; at: string; revision: number; reason?: string }[];
  /** Respuestas guardadas que todavía no se repartieron en piezas. */
  pending_inputs: number;
  cv_id: string | null;
}

/** `gap_id`: la tarjeta del lote que frenó el guardado (422 `answer_is_question`). */
export type ProfileError = { status: 'error'; code: string; message: string; gap_id?: string };

export type ProfileState = { status: 'ok'; view: ProfileView } | { status: 'no_profile' } | ProfileError;

export type ProfileWriteState =
  | {
      status: 'ok';
      view: ProfileView;
      previous_score: number | null;
      score: number | null;
      /** Por qué bajó, cuando bajó (editar o borrar). */
      reason: string | null;
      /**
       * false: lo guardado todavía no entró a las piezas (el modelo no lo repartió
       * o procesar se cayó). El puntaje de antes no es «se mantuvo», es «todavía
       * no sabemos»: la pantalla dice que está actualizando y vuelve a preguntar.
       */
      processed: boolean;
    }
  | { status: 'no_profile' }
  | ProfileError;

const BASE = '/job-apply/api/profile';

/** El código del servidor → la frase en el idioma activo. */
function errorFor(code: string): ProfileError {
  const t = messages().onboarding;
  const message =
    code === 'network'
      ? t.client.network
      : code === 'profile_busy'
        ? t.improveCV.busy
        : code === 'unknown_gap'
          ? t.improveCV.gapGone
          : code === 'answer_is_question'
            ? t.improveCV.answerIsQuestion
            : code === 'invalid_body'
              ? t.improveCV.checkFields
              : code === 'no_change'
                ? t.improveCV.noChange
                : code === 'singleton_exists'
                  ? t.improveCV.singletonExists
                  : t.improveCV.saveFailed;
  return { status: 'error', code, message };
}

async function call(path: string, init?: RequestInit): Promise<{ res: Response; body: Record<string, any> | null } | ProfileError> {
  let res: Response;
  try {
    res = await apiFetch(path, init);
  } catch {
    return errorFor('network');
  }
  const body = (await res.json().catch(() => null)) as Record<string, any> | null;
  return { res, body };
}

function isError(x: unknown): x is ProfileError {
  return !!x && (x as ProfileError).status === 'error';
}

export async function fetchProfile(): Promise<ProfileState> {
  const r = await call(BASE);
  if (isError(r)) return r;
  const { res, body } = r;
  if (res.status === 404 && (!body?.code || body.code === 'no_profile')) return { status: 'no_profile' };
  if (!res.ok || !body?.view) {
    const failed = errorFor(String(body?.code ?? 'profile_failed'));
    return { ...failed, message: messages().onboarding.improveCV.loadFailed };
  }
  return { status: 'ok', view: body.view as ProfileView };
}

async function write(path: string, method: string, payload?: unknown): Promise<ProfileWriteState> {
  const r = await call(path, {
    method,
    ...(payload !== undefined
      ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) }
      : {}),
  });
  if (isError(r)) return r;
  const { res, body } = r;
  if (res.status === 404 && body?.code === 'no_profile') return { status: 'no_profile' };
  if (!res.ok || !body?.view) {
    const failed = errorFor(String(body?.code ?? 'profile_failed'));
    return typeof body?.gap_id === 'string' ? { ...failed, gap_id: body.gap_id } : failed;
  }
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    status: 'ok',
    view: body.view as ProfileView,
    previous_score: num(body.previous_score),
    score: num(body.score),
    reason: typeof body.reason === 'string' && body.reason.trim() ? body.reason : null,
    // Un servidor de antes no lo manda: entonces sí se procesó.
    processed: body.processed !== false,
  };
}

const gapPath = (gapId: string, action: 'answer' | 'dismiss') => `${BASE}/gaps/${encodeURIComponent(gapId)}/${action}`;
const piecePath = (pieceId: string) => `${BASE}/pieces/${encodeURIComponent(pieceId)}`;

/** Responder una carencia. El texto entra entero al inbox antes de cualquier modelo. */
export function answerGap(gapId: string, text: string, inputMode: InputMode): Promise<ProfileWriteState> {
  return write(gapPath(gapId, 'answer'), 'POST', { text, input_mode: inputMode });
}

/**
 * Todas las tarjetas de una vez: respuestas y «No tengo esto» en una sola
 * escritura, y el servidor procesa (y recalcula el puntaje) una sola vez.
 */
export function answerGaps(
  answers: { gap_id: string; text: string; input_mode: InputMode }[],
  dismiss: string[],
): Promise<ProfileWriteState> {
  return write(`${BASE}/gaps/batch`, 'POST', { answers, dismiss });
}

/** «No tengo esto»: la carencia deja de mostrarse. */
export function dismissGap(gapId: string): Promise<ProfileWriteState> {
  return write(gapPath(gapId, 'dismiss'), 'POST');
}

export function addPiece(kind: PieceKind, fields: Record<string, unknown>, text: string): Promise<ProfileWriteState> {
  return write(`${BASE}/pieces`, 'POST', { kind, fields, text });
}

/** Solo las llaves que cambian: el servidor reemplaza esas y deja el resto. */
export function editPiece(
  pieceId: string,
  patch: { fields?: Record<string, unknown>; text?: string },
): Promise<ProfileWriteState> {
  return write(piecePath(pieceId), 'PATCH', patch);
}

/** Borrar exige `confirm: true`: la confirmación ya la dio la persona en la tarjeta. */
export function deletePiece(pieceId: string): Promise<ProfileWriteState> {
  return write(piecePath(pieceId), 'DELETE', { confirm: true });
}

export function deleteNote(pieceId: string, noteId: string): Promise<ProfileWriteState> {
  return write(`${piecePath(pieceId)}/notes/${encodeURIComponent(noteId)}`, 'DELETE');
}
